import React, { useContext, useState, useEffect } from 'react';
import classNames from 'classnames';
import { bool } from 'prop-types';

import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { Image } from '@thd-olt-component-react/core-ui';
import { QuantityAdjustmentsContext } from '@thd-olt-component-react/bundling-product-tabs';

import { TotalPrice } from './TotalPrice';
import { PackagesMiniContext } from '../PackagesMiniContext';
import { GM_PACKAGE, KITCHEN_PACKAGE, PRODUCT_POD } from '../../constants';
import './package-price-suite.scss';

const MCC_FULFILLMENT_METHOD_DIRECT_DELIVERY = 'DirectDelivery';
const MCC_FULFILLMENT_METHOD_STH = 'ShipToHome';

const buildCartRequestParams = (selectedProducts, zipCode, isMajorAppliancePackage, itemMap) => {
  const fulfillmentMethod = isMajorAppliancePackage
    ? MCC_FULFILLMENT_METHOD_DIRECT_DELIVERY
    : MCC_FULFILLMENT_METHOD_STH;
  return (selectedProducts) ? selectedProducts.map((itemId) => {
    const quantity = itemMap[itemId]?.selectedQty || 1;
    return {
      itemId,
      quantity,
      fulfillmentMethod,
      fulfillmentLocation: zipCode
    };
  }) : [];
};

const enhanceMetadataOptions = (metadata) => {
  return {
    brandImage: metadata.brandImage?.url || '',
    heroImage: metadata.heroImage?.url || '',
    packageBrand: metadata.brandName,
    packageImage: metadata.image?.url?.replace(/<SIZE>/g, '400') || '',
    packageName: metadata.name
  };
};

const getHostName = () => {
  let host = 'https://www.homedepot.com';
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    host = origin.match(/local/g) ? 'https://www.homedepot.com' : origin;
  }
  return host;
};

const getCartOptions = ({
  isMajorAppliancePackage, selectedProductIds, channel, metadata, anchorItemId
}) => {
  const defaultParams = {
    host: getHostName(),
    channel,
    paypal: false,
    misship: false,
    component: PRODUCT_POD,
    anchorProductSku: anchorItemId,
    recommendationType: isMajorAppliancePackage ? KITCHEN_PACKAGE : GM_PACKAGE,
    sharedSection: isMajorAppliancePackage ? KITCHEN_PACKAGE : GM_PACKAGE
  };
  if (isMajorAppliancePackage && selectedProductIds.length === 1) {
    return defaultParams;
  }
  return { ...defaultParams, packageInfo: { ...metadata, ...enhanceMetadataOptions(metadata) } };
};

const PackagePriceSuite = () => {
  const { channel } = useContext(ExperienceContext);
  const {
    newModel: {
      metadata,
      isMajorAppliancePackage,
      selectedProductIds
    } = {},
    products,
    anchorItemId,
  } = useContext(PackagesMiniContext);

  const { itemMap } = useContext(QuantityAdjustmentsContext);

  const storeZip = typeof window !== 'undefined' && window.cookieUtils
    ? window.cookieUtils.readBrowserCookie?.('DELIVERY_ZIP')
    : '';

  const atcText = 'Add Bundle to Cart';
  const cartReqParams = buildCartRequestParams(selectedProductIds, storeZip, isMajorAppliancePackage, itemMap);
  const cartOptions = getCartOptions({
    isMajorAppliancePackage,
    selectedProductIds,
    channel,
    metadata,
    anchorItemId,
  });
  const priceSuiteClasses = classNames('package-price-suite', {
    'package-price-suite--space': channel === 'mobile'
  });

  const [isAddToCartDisabled, setIsAddToCartDisabled] = useState(false);

  useEffect(() => {
    if (Object.values(itemMap).every(({ availableQty, selectedQty }) => {
      return !availableQty || availableQty >= selectedQty;
    })) {
      setIsAddToCartDisabled(false);
    } else {
      setIsAddToCartDisabled(true);
    }
  }, [itemMap]);

  return (
    <div className={priceSuiteClasses}>
      <div className="package-price-suite__price">
        <p className="package-price-suite__message">
          {'Price for Bundle: '}
        </p>
        <TotalPrice products={products} />
      </div>
      {isMajorAppliancePackage && (
        <div className="package-price-suite__delivery">
          <Image
            src="https://assets.thdstatic.com/images/v1/alert-check-green.svg"
            alt="Free Delivery"
          />
          <h3 className="package-price-suite__delivery-text">Free Delivery</h3>
        </div>
      )}
      <AddToCart
        outline
        cartReqParams={cartReqParams}
        cartOptions={cartOptions}
        disabled={isAddToCartDisabled}
      >
        {atcText}
      </AddToCart>
    </div>
  );
};

PackagePriceSuite.themeProps = {
  hideFinancingOffer: bool
};

PackagePriceSuite.defaultThemeProps = {
  hideFinancingOffer: false
};

PackagePriceSuite.displayName = 'PackagePriceSuite';

export { PackagePriceSuite };
